import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { COLORS } from "../config/themes";
import Icon from "../components/Icon";
import { Badge, Avatar, Button, Modal, InputField, SelectField, StatCard, ExportButtons, TimePicker, AnimatedNumber, SeasonalArt } from "../components";
import { todayStr, exportToCSV, exportToExcel, studentName, ageGroupColor, ageGroupBg, canEdit, canEditAttendance, teacherStatusConfig } from "../utils/helpers";
import { SEASONAL_THEMES } from "../config/themes";
import { ROLE_CONFIG } from "../config/constants";
import { supabase } from "../supabaseClient";

const ImportStudentsModal = ({ open, onClose, schoolYears, activeYearId, classrooms, onImport }) => {
  const [sourceYearId, setSourceYearId] = useState("");
  const [selected, setSelected] = useState({});
  const [targetRoom, setTargetRoom] = useState(classrooms[0]?.id || "");
  const otherYears = schoolYears.filter(y => y.id !== activeYearId);
  const sourceYear = schoolYears.find(y => y.id === sourceYearId);
  const sourceStudents = sourceYear?.data?.students || [];
  const sourceClassrooms = sourceYear?.data?.classrooms || [];
  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const selectAll = () => { const allIds = {}; sourceStudents.forEach(s => allIds[s.id] = true); setSelected(allIds); };
  const selectNone = () => setSelected({});
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const doImport = () => {
    const studentsToImport = sourceStudents.filter(s => selected[s.id]).map(s => ({
      ...s, id: Date.now() + Math.random(), classroomId: targetRoom, enrollmentDate: todayStr(), withdrawalDate: ""
    }));
    if (studentsToImport.length > 0) onImport(studentsToImport);
    onClose();
  };
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Import Students from Previous Year" wide>
      {otherYears.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: COLORS.textMuted }}>No previous school years found. Create a school year and add students first.</div>
      ) : (<div>
        <SelectField label="Source School Year" value={sourceYearId} onChange={e => { setSourceYearId(e.target.value); setSelected({}); }}
          options={[{ value: "", label: "Select a year..." }, ...otherYears.map(y => ({ value: y.id, label: `${y.name} (${y.data?.students?.length || 0} students)` }))]} />
        {sourceStudents.length > 0 && (<>
          <SelectField label="Import Into Classroom" value={targetRoom} onChange={e => setTargetRoom(e.target.value)}
            options={classrooms.map(c => ({ value: c.id, label: c.name }))} />
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={selectAll} style={{ padding: "4px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", color: COLORS.primary }}>Select All</button>
            <button onClick={selectNone} style={{ padding: "4px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", color: COLORS.textMuted }}>Clear</button>
            <span style={{ fontSize: 11, color: COLORS.textMuted, alignSelf: "center" }}>{selectedCount} selected</span>
          </div>
          <div style={{ maxHeight: 300, overflow: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
            {sourceStudents.map(s => { const sc = sourceClassrooms.find(c => c.id === s.classroomId); return (
              <div key={s.id} onClick={() => toggle(s.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", background: selected[s.id] ? COLORS.primaryPale : "transparent", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                <input type="checkbox" checked={!!selected[s.id]} readOnly style={{ accentColor: COLORS.primary }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{studentName(s)}</div><div style={{ fontSize: 11, color: COLORS.textMuted }}>{sc?.name || "Unknown"} · DOB: {s.dob}</div></div>
              </div>); })}
          </div>
          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button disabled={selectedCount === 0} onClick={doImport}>Import {selectedCount} Student{selectedCount !== 1 ? "s" : ""}</Button>
          </div>
        </>)}
      </div>)}
    </Modal>
  );
};

const LoginScreen = ({ onLogin, schoolSettings, currentTheme, season, onDemoLogin, initialMode, onShowLanding, prefillEmail, prefillName }) => {
  const [mode, setMode] = useState(initialMode === "signup" ? "signup" : "login");
  const [email, setEmail] = useState(prefillEmail || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(prefillName || "");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleLogin = async () => {
    if (!email.trim() || !password) { setError("Please enter email and password"); return; }
    setLoading(true); setError("");
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (authError) { setError(authError.message); setLoading(false); return; }
      // onLogin will be called by the auth state listener in App.jsx
    } catch (e) { setError("An unexpected error occurred"); }
    setLoading(false);
  };

  const handleSignup = async () => {
    if (!name.trim()) { setError("Please enter your full name"); return; }
    if (!email.trim()) { setError("Please enter your email"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords don't match"); return; }
    setLoading(true); setError("");
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { name: name.trim() } }
      });
      if (authError) { setError(authError.message); setLoading(false); return; }
      if (data?.user?.identities?.length === 0) {
        setError("An account with this email already exists");
        setLoading(false); return;
      }
      // Show "check your email" screen
      setMode("pending"); setPassword(""); setConfirmPassword("");
    } catch (e) { setError("An unexpected error occurred"); }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError("Please enter your email address"); return; }
    setLoading(true); setError("");
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin
      });
      if (resetError) { setError(resetError.message); setLoading(false); return; }
      setMessage("Password reset email sent! Check your inbox.");
      setMode("login");
    } catch (e) { setError("An unexpected error occurred"); }
    setLoading(false);
  };

  return (
    <div style={{ fontFamily: "'Nunito', 'DM Sans', system-ui, sans-serif", background: currentTheme?.loginGradient || "linear-gradient(135deg, #F4F7FB 0%, #DBEAFE 50%, #E8EEF4 100%)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.text, position: "relative", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Serif+Display&family=Nunito:wght@400;600;700;800&display=swap" rel="stylesheet" />


      <div style={{ background: COLORS.card, borderRadius: 28, padding: 40, width: 420, maxWidth: "90vw", boxShadow: "0 24px 80px rgba(0,0,0,0.08)", border: `1px solid ${COLORS.border}`, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          {schoolSettings?.logo ? <img src={schoolSettings.logo} alt="Logo" style={{ height: 56, borderRadius: 12, marginBottom: 12 }} /> : <div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: "#fff" }}><Icon name="classrooms" size={32} color="#fff" /></div>}
          <h2 style={{ margin: 0, fontSize: 24, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}>{schoolSettings?.schoolName || "School Management Platform"}</h2>
          <p style={{ margin: "6px 0 0", color: COLORS.textMuted, fontSize: 13 }}>
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : mode === "pending" ? "Almost there!" : "Welcome back"}
          </p>
        </div>

        {message && <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.successPale, color: COLORS.success, fontSize: 12, fontWeight: 600, marginBottom: 16, textAlign: "center" }}>{message}</div>}
        {error && <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.dangerPale, color: COLORS.danger, fontSize: 12, fontWeight: 600, marginBottom: 16, textAlign: "center" }}>{error}</div>}

        {/* === SIGN UP === */}
        {mode === "signup" && (<div>
          <InputField label="Full Name" value={name} onChange={e => { setName(e.target.value); setError(""); }} placeholder="e.g. Sarah Johnson" />
          <InputField label="Email" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} placeholder="you@school.edu" />
          <InputField label="Password" type="password" value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="At least 6 characters" />
          <InputField label="Confirm Password" type="password" value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); setError(""); }} placeholder="Re-enter password" onKeyDown={e => e.key === "Enter" && handleSignup()} />
          <button onClick={handleSignup} disabled={loading} style={{ width: "100%", padding: "12px 0", borderRadius: 12, border: "none", background: COLORS.primary, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1, marginTop: 4 }}>
            {loading ? "Creating Account..." : "Create Account"}
          </button>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <span style={{ fontSize: 13, color: COLORS.textMuted }}>Already have an account? </span>
            <button onClick={() => { setMode("login"); setError(""); setMessage(""); }} style={{ background: "none", border: "none", color: COLORS.primary, fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Sign In</button>
          </div>
        </div>)}

        {/* === PENDING EMAIL CONFIRMATION === */}
        {mode === "pending" && (<div style={{ textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: COLORS.successPale, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>📧</div>
          <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: COLORS.navy }}>Check your email</h3>
          <p style={{ margin: "0 0 20px", fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>
            We sent a confirmation link to <strong style={{ color: COLORS.text }}>{email}</strong>.<br />
            Click the link in the email to activate your account.
          </p>
          <div style={{ padding: "12px 16px", borderRadius: 10, background: COLORS.primaryPale, fontSize: 12, color: COLORS.primary, fontWeight: 600, marginBottom: 20 }}>
            After confirming, you'll be taken straight to setup.
          </div>
          <button onClick={() => { setMode("login"); setError(""); setMessage(""); }} style={{ background: "none", border: "none", color: COLORS.primary, fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
            Back to Sign In
          </button>
        </div>)}

        {/* === FORGOT PASSWORD === */}
        {mode === "forgot" && (<div>
          <InputField label="Email" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} placeholder="you@school.edu" onKeyDown={e => e.key === "Enter" && handleForgotPassword()} />
          <button onClick={handleForgotPassword} disabled={loading} style={{ width: "100%", padding: "12px 0", borderRadius: 12, border: "none", background: COLORS.primary, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={() => { setMode("login"); setError(""); setMessage(""); }} style={{ background: "none", border: "none", color: COLORS.primary, fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Back to Sign In</button>
          </div>
        </div>)}

        {/* === LOGIN === */}
        {mode === "login" && (<div>
          <InputField label="Email" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); setMessage(""); }} placeholder="you@school.edu" />
          <InputField label="Password" type="password" value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="Your password" onKeyDown={e => e.key === "Enter" && handleLogin()} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button onClick={() => { setMode("forgot"); setError(""); setMessage(""); }} style={{ background: "none", border: "none", color: COLORS.primary, fontWeight: 600, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Forgot password?</button>
          </div>
          <button onClick={handleLogin} disabled={loading} style={{ width: "100%", padding: "12px 0", borderRadius: 12, border: "none", background: COLORS.primary, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Signing In..." : "Sign In"}
          </button>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <span style={{ fontSize: 13, color: COLORS.textMuted }}>Don't have an account? </span>
            <button onClick={() => { setMode("signup"); setError(""); setMessage(""); }} style={{ background: "none", border: "none", color: COLORS.primary, fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Sign Up</button>
          </div>

          {/* Demo mode divider removed */}
        </div>)}
      </div>
    </div>
  );
};

export { ImportStudentsModal };
export default LoginScreen;
